import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../../hooks/useAuth';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../../gql/client';
import {
  FETCH_PREDEFINED_ORDERS,
  FETCH_SUB_CAPITOLS,
} from '../../../../../gql/queries/orders/predefinedData';
import { GET_SELECTORS_DATA } from '../../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
} from '../../../../../gql/queries/orders/predefinedData';
import { selectOrderByIdForFillFormData } from '../../../../../gql/selectors/orders';
import {
  DuplicateAuthor,
  selectDuplicateAuthor,
} from '../../../../../gql/selectors/duplicateAuthor';

// Data static
import { groupLanes, groupTracks } from '../../../../../data/orders';

// Compression
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { ORDER_DUPLICATE_SCOPE } from '../../../../../utils/draftScopes';
import { readOwnDraftCopy } from '../../../../../utils/ownedDraftCopy';

// naming
import { formOrderNaming } from '../../../../../components/forms/NewWorkOrderForm/constants';

const loadNewWorkOrderSummary = async (
  idOrder: string,
  userData: AuthContextType | null
) => {
  try {
    const workOrderData = await selectOrderByIdForFillFormData(idOrder);
    return {
      ...workOrderData,
      operator: `${userData?.user?.user?.name} ${
        userData?.user?.user?.surName.split(' ')[0]
      }`,
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Work Order Summary loading error:', err);
    return true;
  }
};

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const [
        { data },
        {
          data: { capitols },
        },
        {
          data: { classifications },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { subCapitols },
        },
      ] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
        }),
      ]);

      return {
        ...data,
        capitols,
        classifications,
        groupTracks,
        groupLanes,
        predefinedOrders,
        subCapitols,
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Work Order Summary loading error:', err);
    return true;
  }
};

export const NewWorkOrderDuplicateLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined =
  (userData) =>
  async ({ params }) => {
  // Se reclama SÍNCRONO, antes de cualquier `await`: la copia del duplicado de
  // OTRO origen (atrás, URL a mano) se descarta aquí y no pisa los datos del
  // origen de esta URL. Sin `:id` (paso sin origen) no hay dueño que reclamar.
  claimDraftScope(ORDER_DUPLICATE_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    let dataFromWorkOrderToDuplicate = {};
    // «Operario» editable del duplicado: arranca en el autor de la original.
    let author: DuplicateAuthor | null = null;

    if (params.id) {
      dataFromWorkOrderToDuplicate = await loadNewWorkOrderSummary(
        params.id,
        userData
      );
      author = await selectDuplicateAuthor(params.id, userData);
    }

    // Pick the cached form values from local storage in case browser was reloaded
    const sessionStorageData = readOwnDraftCopy(
      formOrderNaming.NEW_DUPLICATE_WORK_ORDER_FORM,
      params.id,
      sessionStorage,
      { allowWithoutEntity: true }
    );

    const loaderToResolve = await loadFormOptions(userData);

    if (sessionStorageData) {

      const combinedData = {
        ...loaderToResolve,
        ...dataFromWorkOrderToDuplicate,
        // Antes del borrador: si el operario eligió otro, gana el suyo.
        ...(author && { userId: author.userId, operator: author.operator }),
        ...sessionStorageData,
        // Después: la lista es la de ahora, no la que quedó en el borrador.
        ...(author && { appUserOptions: author.appUserOptions }),
      };

      return combinedData;
    }

    const nameOperator = `${userData?.user?.user.name} ${
      userData?.user?.user.surName.split(' ')[0]
    }`;
    const combinedData = {
      ...loaderToResolve,
      ...dataFromWorkOrderToDuplicate,
      operator: nameOperator,
      // Sin borrador: autor y nombre de la ORIGINAL, no el logueado.
      ...author,
    };

    // Prepare some default values for the form to load properly
    return combinedData;
  };

  return { data: load() };
};
